import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useToast } from '../../contexts';
import { supabase } from '../../lib/supabase';
import { UnifiedLoadingSpinner } from '../ui';

interface ForgotPasswordFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
  showTitle?: boolean;
}

const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({ 
  onSuccess, 
  onCancel, 
  showTitle = true 
}) => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formErrors, setFormErrors] = useState<{ email?: string }>({});
  const [lastRequestTime, setLastRequestTime] = useState<number>(0);
  const [isSupabaseRateLimit, setIsSupabaseRateLimit] = useState(false);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const RATE_LIMIT_DELAY = 60000; // 1分間のレート制限
  const SUPABASE_RATE_LIMIT_DELAY = 900000; // 15分間（Supabaseメール送信制限時）



  // Auto-focus on email input
  useEffect(() => {
    if (emailInputRef.current) {
      emailInputRef.current.focus();
    }
  }, []);

  // レート制限チェック
  const checkRateLimit = (): boolean => {
    const now = Date.now();
    const timeSinceLastRequest = now - lastRequestTime;
    const activeDelay = isSupabaseRateLimit ? SUPABASE_RATE_LIMIT_DELAY : RATE_LIMIT_DELAY;
    
    if (lastRequestTime > 0 && timeSinceLastRequest < activeDelay) {
      const remaining = Math.ceil((activeDelay - timeSinceLastRequest) / 1000);
      const remainingMinutes = Math.ceil(remaining / 60);
      
      if (isSupabaseRateLimit) {
        showError(`メール送信制限に達しています。約${remainingMinutes}分後に再試行してください。`, 'メール送信制限');
      } else {
        showError(`短時間での連続リクエストは制限されています。${remaining}秒後に再試行してください。`, 'レート制限');
      }
      return false;
    }
    return true;
  };

  // フォームバリデーション
  const validateForm = (): boolean => {
    const errors: { email?: string } = {};

    if (!email) {
      errors.email = 'メールアドレスを入力してください';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = '有効なメールアドレスを入力してください';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // 入力値変更ハンドラー
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setEmail(value);

    // エラーをクリア
    if (formErrors.email) {
      setFormErrors({});
    }
  };

  // フォーム送信ハンドラー
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!checkRateLimit()) {
      return;
    }

    // 制限が解除されている場合、Supabaseレート制限フラグをリセット
    const now = Date.now();
    const timeSinceLastRequest = now - lastRequestTime;
    if (isSupabaseRateLimit && timeSinceLastRequest >= SUPABASE_RATE_LIMIT_DELAY) {
      setIsSupabaseRateLimit(false);
    }

    setIsLoading(true);
    setLastRequestTime(Date.now());

    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/callback`,
      });

      if (error) {
        throw error;
      }

      // Supabaseのレスポンスを詳しく確認
      // 実際には、登録されていないメールアドレスでも成功レスポンスが返される
      // これはセキュリティ上の理由（ユーザーの存在を外部に漏らさないため）
      
      // レスポンスの詳細をログに出力（デバッグ用）
      if (data) {
        // デバッグ用のログ（本番環境では削除）
      }
      
      // より適切なメッセージを表示
      // 実際のメール送信の有無に関係なく、リクエストは処理されたことを示す
      setIsSubmitted(true);
      showSuccess(
        'パスワードリセットのリクエストを処理しました。',
        'リクエスト完了'
      );
      onSuccess?.();
    } catch (err: any) {
      // エラーメッセージを適切に処理
      let errorMessage = 'リクエストの処理中にエラーが発生しました。しばらく時間をおいてから再試行してください。';
      let errorTitle = 'エラー';
      
      if (err.message) {
        const message = err.message.toLowerCase();
        
        if (message.includes('email rate limit exceeded')) {
          errorMessage = 'メール送信の制限に達しました。しばらく時間をおいてから再試行してください（通常15分〜1時間）。';
          errorTitle = 'メール送信制限';
          setIsSupabaseRateLimit(true); // Supabaseのメール送信制限フラグを設定
        } else if (message.includes('rate limit')) {
          errorMessage = 'リクエストが多すぎます。しばらく時間をおいてから再試行してください。';
          errorTitle = '制限エラー';
        } else if (message.includes('too many requests')) {
          errorMessage = 'リクエストが多すぎます。時間をおいてから再試行してください。';
          errorTitle = '制限エラー';
        }
      }
      
      showError(errorMessage, errorTitle);
    } finally {
      setIsLoading(false);
    }
  };

  // ログインページに戻る
  const handleBackToLogin = () => {
    navigate('/auth?mode=login');
  };

  if (isSubmitted) {
    return (
      <div className="w-full max-w-md mx-auto text-center">
        <div className="mb-6">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
            <svg
              className="h-6 w-6 text-green-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h3 className="mt-4 text-lg font-medium text-gray-900">
            リクエスト完了
          </h3>
          <p className="mt-2 text-sm text-gray-600">
            パスワードリセットのリクエストを処理しました。
          </p>
          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md">
            <p className="text-sm text-blue-800 font-medium mb-2">次のステップ：</p>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• このメールアドレスが登録されている場合、リセット用のリンクをお送りします</li>
              <li>• メールが届かない場合は、迷惑メールフォルダもご確認ください</li>
              <li>• セキュリティのため、メールアドレスの存在確認は行っていません</li>
            </ul>
          </div>
        </div>
        <div className="space-y-3">
          <button
            type="button"
            onClick={handleBackToLogin}
            className="w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
          >
            ログインページに戻る
          </button>
          <button
            type="button"
            onClick={() => {
              setEmail('');
              setIsSubmitted(false);
              setFormErrors({});
            }}
            className="w-full py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
          >
            別のメールアドレスで再試行
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md mx-auto">
      {showTitle && (
        <h2 className="text-2xl font-bold text-center mb-6 text-gray-800">
          ログインにお困りの方
        </h2>
      )}

      <p className="text-center text-gray-600 mb-6">
        メールアドレスを入力してください。
        パスワードリセット用のリンクをお送りします。
      </p>
      <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-md">
        <p className="text-xs text-gray-600">
          <strong>注意：</strong>セキュリティのため、メールアドレスの存在確認は行っていません。
          登録されていないメールアドレスの場合でも、リクエストは正常に処理されます。
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4" data-testid="forgot-password-form">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            メールアドレス
          </label>
          <input
            ref={emailInputRef}
            type="email"
            id="email"
            name="email"
            value={email}
            onChange={handleInputChange}
            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-blue-500 ${
              formErrors.email ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="example@email.com"
            disabled={isLoading}
            required
            data-testid="email-input"
          />
          {formErrors.email && (
            <p className="mt-1 text-sm text-red-600">{formErrors.email}</p>
          )}
        </div>

        {/* レート制限の残り時間表示 */}
        {lastRequestTime > 0 && (() => {
          const now = Date.now();
          const timeSinceLastRequest = now - lastRequestTime;
          const activeDelay = isSupabaseRateLimit ? SUPABASE_RATE_LIMIT_DELAY : RATE_LIMIT_DELAY;
          const remaining = Math.ceil((activeDelay - timeSinceLastRequest) / 1000);
          
          if (timeSinceLastRequest < activeDelay) {
            if (isSupabaseRateLimit) {
              const remainingMinutes = Math.ceil(remaining / 60);
              return (
                <div className="text-xs text-orange-600 text-center mb-4 p-2 bg-orange-50 rounded">
                  メール送信制限中：約 {remainingMinutes} 分待機中
                </div>
              );
            } else {
              return (
                <div className="text-xs text-gray-500 text-center mb-4">
                  次のリクエストまで {remaining} 秒待機中
                </div>
              );
            }
          }
          return null;
        })()}

        <div className="flex space-x-3 pt-6">
          <button
            type="submit"
            disabled={isLoading || (() => {
              const now = Date.now();
              const timeSinceLastRequest = now - lastRequestTime;
              const activeDelay = isSupabaseRateLimit ? SUPABASE_RATE_LIMIT_DELAY : RATE_LIMIT_DELAY;
              return lastRequestTime > 0 && timeSinceLastRequest < activeDelay;
            })()}
            data-testid="submit-button"
            className={`flex-1 py-2 px-4 rounded-md text-white font-medium focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 flex items-center justify-center ${
              isLoading || (() => {
                const now = Date.now();
                const timeSinceLastRequest = now - lastRequestTime;
                const activeDelay = isSupabaseRateLimit ? SUPABASE_RATE_LIMIT_DELAY : RATE_LIMIT_DELAY;
                return lastRequestTime > 0 && timeSinceLastRequest < activeDelay;
              })() ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'
            }`}
          >
            {isLoading ? (
              <>
                <UnifiedLoadingSpinner size="sm" color="white" variant="spinner" className="mr-2" />
                <span role="status">送信中</span>
              </>
            ) : (() => {
              const now = Date.now();
              const timeSinceLastRequest = now - lastRequestTime;
              const activeDelay = isSupabaseRateLimit ? SUPABASE_RATE_LIMIT_DELAY : RATE_LIMIT_DELAY;
              const remaining = Math.ceil((activeDelay - timeSinceLastRequest) / 1000);
              
              if (lastRequestTime > 0 && timeSinceLastRequest < activeDelay) {
                if (isSupabaseRateLimit) {
                  const remainingMinutes = Math.ceil(remaining / 60);
                  return `制限中 (${remainingMinutes}分)`;
                } else {
                  return `待機中 (${remaining}s)`;
                }
              }
              return 'リセットリンク送信';
            })()}
          </button>

          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={isLoading}
              className="flex-1 py-2 px-4 border border-gray-300 rounded-md text-gray-700 font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              キャンセル
            </button>
          )}
        </div>
      </form>

      <div className="mt-6 text-center text-sm text-gray-600">
        アカウントをお持ちの方は{' '}
        <button
          onClick={handleBackToLogin}
          className="font-medium text-blue-600 hover:text-blue-500"
        >
          ログイン
        </button>
      </div>
    </div>
  );
};

export default ForgotPasswordForm;
